import type { Geotab } from "@fairfleet/geotab";

import AddInIds from "@/constants/AddInIds";
import { RESULTS_LIMIT_ADD_IN_DATA } from "@/constants/resultsLimits";
import { pickNewestPerKey, upsertAddInData } from "@/utilities/addInData/upsertAddInData";
import { decompressData } from "@/utilities/compression";

export interface UserStateRow {
  geotabId: string;
  key: string;
  updatedAt: number;
  payload: unknown;
}

interface RawRow {
  id: string;
  details: unknown;
}

interface Decoded {
  id: string;
  details: { updatedAt?: number };
  key: string;
  payload: unknown;
}

export const recordKey = (userId: string, key: string) => `${userId}::${key}`;

function decodeRow(raw: RawRow, userId: string): Decoded | null {
  const details = raw.details as {
    userId?: unknown;
    recordKey?: unknown;
    updatedAt?: unknown;
    cmp?: unknown;
  };
  if (!details || details.userId !== userId) return null;
  const prefix = `${userId}::`;
  if (typeof details.recordKey !== "string" || !details.recordKey.startsWith(prefix)) return null;
  if (typeof details.cmp !== "string") return null;
  try {
    const payload = decompressData<unknown>({ cmp: details.cmp });
    const updatedAt = typeof details.updatedAt === "number" ? details.updatedAt : 0;
    return {
      id: raw.id,
      details: { updatedAt },
      key: details.recordKey.slice(prefix.length),
      payload,
    };
  } catch {
    return null;
  }
}

export function decodeUserStateRows(
  raw: RawRow[],
  userId: string,
): { rows: UserStateRow[]; duplicateIds: string[] } {
  const decoded = raw.map(r => decodeRow(r, userId)).filter((r): r is Decoded => r !== null);
  const { keep, duplicates } = pickNewestPerKey(decoded, r => r.key);
  return {
    rows: keep.map(r => ({
      geotabId: r.id,
      key: r.key,
      updatedAt: r.details.updatedAt ?? 0,
      payload: r.payload,
    })),
    duplicateIds: duplicates.map(r => r.id),
  };
}

export async function fetchUserStateRows(
  geotabClient: Geotab,
  userId: string,
  signal?: AbortSignal,
): Promise<{ rows: UserStateRow[] }> {
  if (userId.includes('"')) throw new Error("invalid userId");
  const raw = (await geotabClient.get(
    "AddInData",
    { addInId: AddInIds.userState, whereClause: `userId = "${userId}"` },
    RESULTS_LIMIT_ADD_IN_DATA,
    signal,
  )) as unknown as RawRow[];
  const { rows, duplicateIds } = decodeUserStateRows(raw, userId);
  // Once per fetch, not per consumer of the hook. Fire-and-forget: a failed cleanup must not fail the read.
  for (const id of duplicateIds) {
    Promise.resolve(geotabClient.call("Remove", { typeName: "AddInData", entity: { id } })).catch(
      (error: unknown) => console.warn("Could not remove duplicate userState row", id, error),
    );
  }
  return { rows };
}

export function writeUserState(
  geotabClient: Geotab,
  userId: string,
  key: string,
  payload: unknown,
): Promise<string> {
  const rk = recordKey(userId, key);
  return upsertAddInData({
    geotabClient,
    addInId: AddInIds.userState,
    lookup: { field: "recordKey", value: rk },
    plain: { userId, recordKey: rk, updatedAt: Date.now() },
    payload,
  });
}
